import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';

import { KeysPipe } from '../../pipes/keys.pipe';
import { PipesModule } from '../../pipes/pipes.module';

type Config = {
  length: number;
  allowNumbersOnly: boolean;
  isPasswordInput: boolean;
};

@Component({
  selector: 'via-otp-input',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, PipesModule],
  providers: [PipesModule],
  templateUrl: './otp-input.component.html',
  styleUrls: ['./otp-input.component.scss'],
})
export class OtpInputComponent implements OnInit, AfterViewInit {
  @Input() config: Config = {
    length: 6,
    allowNumbersOnly: true,
    isPasswordInput: false,
  };
  // tslint:disable-next-line: no-output-on-prefix
  @Output() inputChange = new EventEmitter<string>();
  @Output() lastInputEnter = new EventEmitter();
  otpForm!: FormGroup;
  currentVal = '';
  inputControls: FormControl[] = new Array(this.config.length);
  componentKey =
    Math.random().toString(36).substring(2) + new Date().getTime().toString(36);

  get inputType() {
    return this.config?.isPasswordInput
      ? 'password'
      : this.config?.allowNumbersOnly
      ? 'tel'
      : 'text';
  }

  constructor(private keysPipe: KeysPipe) {}

  ngOnInit() {
    this.otpForm = new FormGroup({});
    for (let index = 0; index < this.config.length; index++) {
      this.otpForm.addControl(this.getControlName(index), new FormControl());
    }
    this.otpForm.valueChanges.subscribe(() => {
      this.keysPipe.transform(this.otpForm.controls).forEach((k) => {
        const val = this.otpForm.controls[k].value;
        if (val && val.length > 1) {
          if (val.length >= this.config.length) {
            this.setValue(val);
          } else {
            this.rebuildValue();
          }
        }
      });
    });
  }

  ngAfterViewInit(): void {
    const containerItem = document.getElementById(`c_${this.componentKey}`);
    if (containerItem) {
      const [ele] = containerItem.getElementsByClassName('otp-input') as any;
      if (ele && ele.focus) {
        ele.focus();
      }
    }
  }

  private getControlName(idx: number) {
    return `ctrl_${idx}`;
  }

  onKeyDown(event: KeyboardEvent) {
    if (this.ifSpacebar(event)) {
      event.preventDefault();
    }
    const controlId = (event.target as any).id;
    if (
      controlId === `otp_${this.config.length - 1}_${this.componentKey}` &&
      this.ifEnter(event)
    ) {
      this.lastInputEnter.emit();
    }
  }

  onInput(event: KeyboardEvent | any) {
    const newVal = this.currentVal
      ? `${this.currentVal}${event.target.value}`
      : event.target.value;
    if (this.config.allowNumbersOnly && !this.validateNumber(newVal)) {
      event.target.value = '';
      event.stopPropagation();
      event.preventDefault();
      return;
    }
  }

  onKeyUp(event: KeyboardEvent | any, inputIdx: number) {
    const nextInputId = this.appendKey(`otp_${inputIdx + 1}`);
    const prevInputId = this.appendKey(`otp_${inputIdx - 1}`);
    if (this.ifRightArrow(event)) {
      event.preventDefault();
      this.setSelected(nextInputId);
      return;
    }
    if (this.ifLeftArrow(event)) {
      event.preventDefault();
      this.setSelected(prevInputId);
      return;
    }
    if (this.ifBackspaceOrDelete(event) && !event.target.value) {
      this.setSelected(prevInputId);
      this.rebuildValue();
      return;
    }

    if (!event.target.value) {
      return;
    }

    this.setSelected(nextInputId);

    this.rebuildValue();
  }

  validateNumber(val: string) {
    return val && /^\d*\.?\d*$/.test(val);
  }

  appendKey(id: string) {
    return `${id}_${this.componentKey}`;
  }

  setSelected(eleId: string) {
    this.focusTo(eleId);
    const ele: any = document.getElementById(eleId);
    if (ele && ele.setSelectionRange) {
      setTimeout(() => {
        ele.setSelectionRange(0, 1);
      }, 0);
    }
  }

  focusTo(eleId: string) {
    const ele: any = document.getElementById(eleId);
    if (ele) {
      ele.focus();
    }
  }

  // method to set component value
  setValue(value: any) {
    if (this.config.allowNumbersOnly && isNaN(value)) {
      return;
    }
    this.otpForm.reset();
    if (!value) {
      this.rebuildValue();
      return;
    }
    value = value.toString().replace(/\s/g, ''); // remove white space
    Array.from(value).forEach((c, idx) => {
      if (this.otpForm.get(this.getControlName(idx))) {
        this.otpForm.get(this.getControlName(idx))?.setValue(c);
      }
    });
    this.rebuildValue();
  }

  rebuildValue() {
    let val = '';
    this.keysPipe.transform(this.otpForm.controls).forEach((k: string) => {
      if (this.otpForm.controls[k].value) {
        let ctrlVal = this.otpForm.controls[k].value;
        const isLengthExceed = ctrlVal.length > 1;

        [ctrlVal] = ctrlVal;

        val += ctrlVal;
        if (isLengthExceed) {
          this.otpForm.controls[k].setValue(ctrlVal);
        }
      }
    });
    this.inputChange.emit(val);
    this.currentVal = val;
  }

  ifBackspaceOrDelete(event: KeyboardEvent) {
    return this.ifKey(event, 'Backspace;Delete;Del');
  }

  ifRightArrow(event: KeyboardEvent) {
    return this.ifKey(event, 'ArrowRight;Right');
  }

  ifLeftArrow(event: KeyboardEvent) {
    return this.ifKey(event, 'ArrowLeft;Left');
  }

  ifSpacebar(event: KeyboardEvent) {
    return this.ifKey(event, 'Spacebar; '); //don't remove the space after ; as this will check for space key
  }

  ifKey(event: KeyboardEvent, keys: string): boolean {
    const keysToCheck = keys.split(';');
    return keysToCheck.some((k) => k === event.key);
  }

  ifEnter(event: KeyboardEvent) {
    return this.ifKey(event, 'Enter');
  }
}
